import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { TrendingDown, RefreshCw } from "lucide-react";

// Registration funnel (own DB, not GA): waitlist → registered → verified → paid → in a team.
// Each bar is sized relative to the largest stage; conversion % is vs the previous stage.
// The stage with the biggest drop-off (lowest conversion from the prior stage) is flagged red.
export default function FunnelCard() {
  const [stages, setStages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/funnel");
      setStages(data.stages || []);
      setUpdatedAt(new Date());
    } catch (_) {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  if (!stages) return null;
  const max = Math.max(1, ...stages.map((s) => s.count));

  // Find the biggest leak: the stage with the lowest conversion% from the previous stage
  // (only counts stages where the previous stage had at least 1 person).
  let worstIdx = -1;
  let worstConv = Infinity;
  stages.forEach((s, i) => {
    if (i === 0) return;
    const prev = stages[i - 1].count;
    if (prev > 0) {
      const conv = s.count / prev;
      if (conv < worstConv) { worstConv = conv; worstIdx = i; }
    }
  });

  return (
    <div className="card-surface mt-4" data-testid="funnel-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-brand" strokeWidth={1.5} />
          <div className="label-mono">Registration Funnel</div>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span className="text-[11px] font-mono text-gray-500" data-testid="funnel-updated-at">
              Updated {updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={fetchFunnel}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gray-400 hover:text-brand disabled:opacity-50 transition-colors"
            data-testid="funnel-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {stages.map((s, i) => {
          const prev = i > 0 ? stages[i - 1].count : null;
          const conv = prev && prev > 0 ? Math.round((s.count / prev) * 100) : null;
          const width = Math.max(2, Math.round((s.count / max) * 100));
          const isLeak = i === worstIdx;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className={isLeak ? "text-red-400" : "text-gray-300"}>
                  {s.label}
                  {isLeak && (
                    <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-400/40 px-1.5 py-0.5" data-testid="funnel-biggest-drop">
                      Biggest drop
                    </span>
                  )}
                </span>
                <span className="font-mono text-white">
                  {s.count}
                  {conv !== null && (
                    <span className={`ml-2 ${isLeak ? "text-red-400" : "text-gray-500"}`} data-testid={`funnel-conv-${s.key}`}>
                      ({conv}%)
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 bg-white/5 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${isLeak ? "bg-red-500" : "bg-brand"}`}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-500 mt-4">
        Live counts from this edition's data. % = conversion from the previous stage; the biggest leak is flagged in red.
      </p>
    </div>
  );
}
